import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-2xl border border-transparent text-sm font-medium whitespace-nowrap outline-none transition-all focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-60 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 active:translate-y-px",
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-lg shadow-primary/20 hover:bg-primary/85',
        secondary:
          'bg-secondary text-secondary-foreground ring-1 ring-foreground/10 hover:bg-secondary/80',
        outline:
          'bg-card/65 text-foreground ring-1 ring-foreground/15 backdrop-blur-sm hover:bg-card/85 hover:ring-foreground/25',
        ghost:
          'bg-transparent text-foreground hover:bg-foreground/5',
        destructive:
          'bg-destructive text-white shadow-lg shadow-destructive/25 hover:bg-destructive/85',
        link:
          'bg-transparent text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-11 px-4 text-sm',
        sm: 'h-9 px-3 text-[13px]',
        lg: 'h-12 px-5 text-[15px]',
        xl: 'h-14 px-6 text-base font-semibold',
        icon: 'size-11',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
